import React from 'react';
import type { ReactNode } from 'react';
import { styled } from '@components-shared/src/stitches';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import { useChartCardContext } from './Context';

export const ChartCardInfo = React.memo(function ChartCardInfo({
  subheader,
  header,
  stats,
}: {
  /**
   * Name of the artist can go here
   */
  subheader: ReactNode;
  /**
   * Name of the release can go here. React node so that we can pass buttons, links etc.
   */
  header: ReactNode;
  /**
   * Stats can go here. React node so we can pass component.
   */
  stats: JSX.Element | null;
}) {
  const { isPlaying, isLoadingSummary, minimal } = useChartCardContext();
  const { tablet } = MediaQuery.useContainer();

  if (isLoadingSummary) {
    return (
      <Container loading={isLoadingSummary}>
        <SubheaderContainer loading={isLoadingSummary} />
        <HeaderContainerWrapper>
          <HeaderContainer loading={isLoadingSummary} />
        </HeaderContainerWrapper>
      </Container>
    );
  }

  return (
    <Container loading={isLoadingSummary} minimal={minimal}>
      <SubheaderContainer>{subheader}</SubheaderContainer>
      <HeaderContainerWrapper>
        {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
        <HeaderContainer>{header}</HeaderContainer>
      </HeaderContainerWrapper>
      {stats}
    </Container>
  );
});

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'all',
  overflow: 'hidden',
  flexShrink: 1,
  justifyContent: 'flex-start',
  alignItems: 'start',
  maxWidth: '100%',

  compoundVariants: [
    {
      size: 'tablet',
      minimal: true,
      css: {
        width: '-webkit-fill-available',
      },
    },
  ],

  variants: {
    size: {
      mobile: {
        paddingRight: 20,
      },
    },
    loading: {
      true: {
        gap: 2,
      },
    },
    minimal: {
      true: {},
    },
  },
});

const SubheaderContainer = styled('div', {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  marginBottom: 4,
  base: 'XS',
  color: '$base500',
  lineHeight: '14px',
  flexShrink: 0,

  '@tablet': {
    base: 'M',
    color: '$base500',
  },

  variants: {
    loading: {
      true: {
        ...LOADING_STYLES,
        height: 16,
        maxWidth: 125,
        width: '100%',
        borderRadius: 100,
      },
    },
  },
});

const HeaderContainerWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: '100%',
});

const HeaderContainer = styled('div', {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  title: 'S',

  '@tablet': {
    title: 'M',
  },

  '&:hover': {
    color: '$black',
  },
  variants: {
    loading: {
      true: {
        ...LOADING_STYLES,
        width: 180,
        height: 20,
        borderRadius: 100,
      },
    },
  },
});
